import type ExemplarDataService from './exemplar.data-service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {Auth, ConfirmableRequest, Department, Document, Exemplar, ExemplarRequest, ViewableExemplar, ViewableExemplarWithDocument, Volume} from 'typings/portaro.be.types';
import type ReloaderService from 'shared/services/reloader.service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';
import ExemplarList from './exemplar-list';
import {Kind, SearchType} from 'shared/constants/portaro.constants';
import bind from 'bind-decorator';
import type {Observable} from 'rxjs';
import {forkJoin, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import type ObservableRecordDataService from './observable-record.data-service';
import type ObservableExemplarDataService from './observable-exemplar.data-service';
import type ModalConversationService from '../../modals/modal-conversation.service';
import type {SearchSelectionModalModel} from '../../modals/search-selection-modal/types';
import type {LocalizationService} from 'shared/providers/localization.provider';
import type {AnyObject} from 'typings/portaro.fe.types';

export type PartialExemplarRequest = Omit<ExemplarRequest, 'document' | 'department'>;

/**
 * @ngdoc service
 * @name exemplarService
 * @module uc.record
 *
 * @description
 *
 */
export default class ExemplarService {
    public static readonly serviceName = 'exemplarService';

    /*@ngInject*/
    constructor(private exemplarDataService: ExemplarDataService,
                private reloader: ReloaderService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private currentAuth: Auth,
                private observableRecordDataService: ObservableRecordDataService,
                private observableExemplarDataService: ObservableExemplarDataService,
                private modalConversationService: ModalConversationService,
                private localizationService: LocalizationService) {
    }

    @ngAsync()
    public async createExemplarByFormAndReload(document: Document, department?: Department) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.save({document, department})
        );
    }

    @ngAsync()
    public async createEmptyExemplar(document: Document, confirmed: boolean) {
        return this.exemplarDataService.save({document, confirmed});
    }

    @ngAsync()
    public async createExemplarFromRequest(document: Document, department?: Department, exemplarRequest?: PartialExemplarRequest) {
        const request = Object.assign<ExemplarRequest, ExemplarRequest, PartialExemplarRequest>({} as ExemplarRequest, {
            document,
            department
        }, exemplarRequest);
        return this.exemplarDataService.save(request);
    }

    @ngAsync()
    public async editExemplar(exemplar: Exemplar) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.save(
                Object.assign<ExemplarRequest, ExemplarRequest, ConfirmableRequest>({} as ExemplarRequest, this.exemplarToExemplarRequest(exemplar), {confirmed: false})
            )
        );
    }

    @ngAsync()
    public async deleteExemplar(exemplar: Exemplar) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.delete([exemplar])
        )
    }

    @ngAsync()
    public async restoreExemplar(exemplar: Exemplar) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.restore(exemplar.id)
        )
    }

    @ngAsync()
    public async createIssue(document: Document, volume: Volume, department?: Department) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.save({document, volume, department})
        )
    }

    @ngAsync()
    public async discardExemplar(exemplar: Exemplar) {
        return this.showResultIgnoringManualInterruptionAndThenReload(
            () => this.exemplarDataService.discard(exemplar.id)
        );
    }

    @ngAsync()
    public async getExemplarList(document: Document, exemplarsView: AnyObject): Promise<ExemplarList> {
        const params = {
            document: document.id,
            allowedExemplarTypes: [0, 2],
            viewable: true,
            user: this.currentAuth.activeUser.id,
            includeDeleted: true
        } as const;

        const exemplars = await this.exemplarDataService.query(params);

        return new ExemplarList(document, exemplars, exemplarsView, this.currentAuth);
    }

    @ngAsync()
    public async searchExemplarViaModal(): Promise<Exemplar> {
        const modalModel: SearchSelectionModalModel = {
            resultPostProcessFunction: this.extendExemplarsOfDocumentAndAvailability,
            staticSearchParams: {
                kind: [Kind.KIND_EXEMPLAR],
                type: SearchType.TYPE_SEARCH_SELECTION
            },
            title: this.localizationService.get(/* @kp-localization exemplar.SelectExemplar */ 'exemplar.SelectExemplar')
        };

        return this.modalConversationService.searchItem<Exemplar>(modalModel);
    }

    /**
     * Extend exemplars to `ViewableExemplar`, load document to it and reset quantity to 0.
     *
     * @param {Exemplar[]} exemplars Exemplars
     * @returns {Observable<ViewableExemplarWithDocument[]>} Extended exemplars.
     * @private
     */
    @bind
    public extendExemplarsOfDocumentAndAvailability(exemplars: Exemplar[]): Observable<ViewableExemplarWithDocument[]> {

        function resetQuantity(ex: ViewableExemplarWithDocument): ViewableExemplarWithDocument {
            ex.quantity = 0;
            return ex
        }

        function extendExemplarWithDocument(exemplar: ViewableExemplar, document: Document): ViewableExemplarWithDocument {
            const extendedExemplar = (exemplar as ViewableExemplarWithDocument);
            extendedExemplar.document = document;
            return extendedExemplar;
        }

        const extendExemplar: ((viewableExemplar: ViewableExemplar) => Observable<ViewableExemplarWithDocument>) = (exemplar) => {
            return this.observableRecordDataService
                .getDocument(exemplar.recordId)
                .pipe(
                    map((document) => extendExemplarWithDocument(exemplar, document)),
                    map(resetQuantity));
        }

        if (exemplars.length === 0) {
            return of([]);
        }

        return this.observableExemplarDataService
            .getViewableExemplars(exemplars)
            .pipe(switchMap((viewableExemplars) => forkJoin(viewableExemplars.map(extendExemplar))));
    }

    private exemplarToExemplarRequest(exemplar: Exemplar): ExemplarRequest {
        return {
            account: exemplar.account,
            acquisitionWay: exemplar.acquisitionWay,
            acquisitionYear: exemplar.acquisitionYear,
            attachments: exemplar.attachments,
            bundledVolumeIssueRange: exemplar.bundledVolumeIssueRange,
            bundledVolumeNumber: exemplar.bundledVolumeNumber,
            bundledVolumeYear: exemplar.bundledVolumeYear,
            creationDate: exemplar.creationDate,
            customValue: exemplar.customValue,
            department: exemplar.department,
            document: exemplar.recordId as unknown as Document,
            id: exemplar.id,
            identifiers: [{
                accessNumber: exemplar.accessNumber,
                barCode: exemplar.barCode,
                signature: exemplar.signature
            }],
            internalNote: exemplar.internalNote,
            invoiceItem: exemplar.invoiceItem,
            invoiceNumber: exemplar.invoiceNumber,
            issueEvidenceNumber: exemplar.issueEvidenceNumber,
            issueName: exemplar.issueName,
            loanCategory: exemplar.loanCategory,
            location: exemplar.location,
            note: exemplar.note,
            order: exemplar.order,
            owner: exemplar.owner,
            price: exemplar.price,
            quantity: exemplar.quantity,
            status: exemplar.status,
            supplier: exemplar.supplier,
            thematicGroup: exemplar.thematicGroup,
            type: exemplar.type,
            volume: exemplar.volumeId as unknown as Volume
        };
    }

    private async showResultIgnoringManualInterruptionAndThenReload(callback: () => Promise<any>) {
        await this.finishedResponseInteractionService.showSuccessToastOrFailedModalIgnoringManualInterruption(() => callback());
        this.reloader.reload();
    }
}